import React, { useMemo, useState } from "react";
import { post, get } from "../lib/api";
import { useLoad, useQueryState } from "../lib/hooks";
import { formatDateTime, formatDate } from "../../helpers/datetime";
import { Badge, Button, Card, Empty, ErrorBox, Loading, Pager, Select } from "../ui";
import { useShell } from "../shell/context";

const ENTITY_LABEL = { companies: "Company", contacts: "Person", roles: "Role", touches: "Touch", tags: "Tag", leads: "Lead", signals: "Signal" };

// Deleted CRM records, restorable for 30 days (GET /v1/crm/trash).
export default function Trash() {
    const { toast, bump, version } = useShell();
    const [page, setPage] = useQueryState("page", 1);
    const [entity, setEntity] = useQueryState("entity", "");
    const [busy, setBusy] = useState(null);
    const { data, error, loading, reload } = useLoad(() => get("v1/crm/trash", { page, page_size: 100 }), [page, version]);
    const rows = useMemo(() => ((data && data.data) || []).filter((r) => !entity || r.entity === entity), [data, entity]);

    const restore = async (r) => {
        setBusy(r.id);
        try {
            await post(`v1/crm/trash/${r.entity}/${r.id}/restore`);
            toast(`${ENTITY_LABEL[r.entity] || r.entity} restored.`);
            bump();
        } catch (e) {
            toast(e.message, { error: true });
        } finally {
            setBusy(null);
        }
    };

    return (
        <Card
            noBody
            title="Trash"
            actions={
                <Select small value={entity} onChange={(e) => setEntity(e.target.value)} placeholder="Everything" options={Object.keys(ENTITY_LABEL).map((k) => ({ value: k, label: ENTITY_LABEL[k] }))} style={{ width: 150 }} />
            }
        >
            <div className="hz-card-section hz-small hz-muted">Deleted records stay here for 30 days, then they are purged for good. Records dropped by a merge cannot be restored.</div>
            <ErrorBox error={error} onRetry={reload} />
            {loading && !data ? (
                <Loading />
            ) : (
                <div className="hz-table-wrap">
                    <table className="hz-table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Record</th>
                                <th>Deleted</th>
                                <th>Purged on</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.entity + r.id}>
                                    <td><Badge color="secondary">{ENTITY_LABEL[r.entity] || r.entity}</Badge></td>
                                    <td>{r.label}</td>
                                    <td className="hz-nowrap">{formatDateTime(r.deleted_at)}</td>
                                    <td className="hz-nowrap hz-muted">{formatDate(r.purge_after)}</td>
                                    <td className="actions">
                                        <Button size="sm" variant="soft" icon="ri-arrow-go-back-line" disabled={busy === r.id} onClick={() => restore(r)}>Restore</Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {rows.length === 0 && !error && <Empty>The trash is empty.</Empty>}
                </div>
            )}
            {data && <Pager page={page} maxPage={data.max_page} count={data.count} onPage={setPage} label="records" />}
        </Card>
    );
}
